/**
 * 認証 API クライアント (Issue #5996)
 *
 * Backend: chat-api Worker の /v1/auth/* (web は /api/v1/auth/* 経由で叩く)
 */
import { clearAuthStorage, retireLegacyAuthStorage } from "@/lib/storage-migration";

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
  expires_at: number; // unix sec
  refresh_expires_at: number;
  tenant?: { id?: string; name?: string; account_type: "personal" | "business" | "unclassified"; ad_eligible: boolean };
  user: {
    id: string;
    tenant_id: string;
    email: string;
    display_name: string;
    role: string;
    last_sign_in_at: number | null;
    sign_in_method: string | null;
  };
}
export interface MfaChallenge {mfa_required:true;challenge_token:string;expires_at:number;methods:('totp'|'recovery_code')[]}
export type AuthResponse=AuthSession|MfaChallenge;
export function isMfaChallenge(value:unknown):value is MfaChallenge {
  const row=value as Partial<MfaChallenge>|null;
  return !!row&&row.mfa_required===true&&typeof row.challenge_token==='string'&&/^[0-9a-f]{64}$/.test(row.challenge_token)&&Number.isFinite(row.expires_at);
}
export function parseMfaFragment(hash:string):MfaChallenge|null {
  const params=new URLSearchParams(hash.replace(/^#/,''));const row={mfa_required:params.get('mfa_required')==='true',challenge_token:params.get('challenge_token'),expires_at:Number(params.get('expires_at')),methods:['totp','recovery_code']};
  return isMfaChallenge(row)?row:null;
}

export interface MagicLinkRequestResult {
  ok: true;
  email: string;
  expires_at: number;
  /** dev mode 時のみ (本番では絶対に返らない) */
  dev_code?: string;
}

export interface ApiErrorResponse {
  error: { code: string; message: string };
}

export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

/**
 * Default base URL for the auth API.
 *
 * - dev: vite proxy で `/api` → :8787 を経由するので `/api/v1` で OK
 * - prod / staging: `VITE_API_BASE` (例: `https://chat-api.aile-ai.jp`) が
 *   定義されているのでそれを優先し、worker を直接叩く
 */
function resolveDefaultBaseUrl(): string {
  const env = (typeof import.meta !== "undefined" ? import.meta.env : undefined) as
    | { VITE_API_BASE?: string }
    | undefined;
  const base = env?.VITE_API_BASE?.replace(/\/$/, "");
  return base ? `${base}/v1` : "/api/v1";
}

const DEFAULT_BASE_URL = resolveDefaultBaseUrl();

export interface AuthApiClient {
  signInMethods?(): Promise<{google:boolean;microsoft:boolean;saml:boolean}>;
  resolveSamlWorkspace?(workspace:string):Promise<{url:string}>;
  requestMagicLink(email: string): Promise<MagicLinkRequestResult>;
  verifyMagicLink(input: {
    email: string;
    code: string;
    display_name?: string;
    tenant_name?: string;
    tenant_slug?: string;
    invitation_token?: string;
  }): Promise<AuthResponse>;
  verifyMfa?(input:{challenge_token:string;code:string}):Promise<AuthSession>;
  refresh(refresh_token: string): Promise<AuthSession>;
  logout(refresh_token: string): Promise<{ ok: boolean; revoked: boolean }>;
  /** Google OAuth 開始 (302 redirect されるので window.location 移動を caller に任せる) */
  googleStartUrl(invitation_token?: string): string;
  microsoftStartUrl(invitation_token?: string): string;
}

export interface ClientOptions {
  baseUrl?: string;
  /** test injection */
  fetchImpl?: typeof fetch;
}

export function createAuthApiClient(options: ClientOptions = {}): AuthApiClient {
  const baseUrl = options.baseUrl ?? DEFAULT_BASE_URL;
  const f = options.fetchImpl ?? fetch.bind(globalThis);

  async function call<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await f(`${baseUrl}${path}`, init);
    const ct = res.headers.get("content-type") ?? "";
    let body: unknown = undefined;
    if (ct.includes("application/json")) {
      try {
        body = await res.json();
      } catch {
        body = undefined;
      }
    }
    if (!res.ok) {
      const err = body as ApiErrorResponse | undefined;
      const code = err?.error?.code ?? `HTTP_${res.status}`;
      const message = err?.error?.message ?? `request failed with ${res.status}`;
      throw new AuthApiError(res.status, code, message);
    }
    return body as T;
  }

  return {
    async signInMethods() {
      // Mac App Store builds use our email-code and Passkey authentication for every user.
      // Google/Microsoft login requires an equivalent private-email option under App Review 4.8;
      // the store shell already identifies its distribution in this stable user-agent token.
      // Both login and workspace invitations read this list, including previously uploaded shells.
      if (typeof navigator !== "undefined" && navigator.userAgent.includes("AileChatMacAppStore/")) {
        return { google: false, microsoft: false, saml: false };
      }
      return call<{ google: boolean; microsoft: boolean; saml: boolean }>("/auth/methods");
    },
    resolveSamlWorkspace:workspace=>call("/auth/saml/resolve",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({workspace})}),
    async requestMagicLink(email) {
      return call<MagicLinkRequestResult>("/auth/magic-link", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
    },
    async verifyMagicLink(input) {
      return call<AuthResponse>("/auth/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });
    },
    async verifyMfa(input){return call<AuthSession>('/auth/mfa/verify',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(input)});},
    async refresh(refresh_token) {
      return call<AuthSession>("/auth/refresh", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token }),
      });
    },
    async logout(refresh_token) {
      return call<{ ok: boolean; revoked: boolean }>("/auth/logout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token }),
      });
    },
    googleStartUrl(invitation_token) {
      return invitation_token
        ? `${baseUrl}/auth/oauth/google?invitation=${encodeURIComponent(invitation_token)}`
        : `${baseUrl}/auth/oauth/google`;
    },
    microsoftStartUrl(invitation_token) {
      return invitation_token
        ? `${baseUrl}/auth/oauth/microsoft?invitation=${encodeURIComponent(invitation_token)}`
        : `${baseUrl}/auth/oauth/microsoft`;
    },
  };
}

// ===========================================================================
// localStorage 経由のセッション保管 (lib/auth.ts と互換)
// ===========================================================================

export const SESSION_STORAGE_KEY = "ailechat:auth-session";
export const SESSION_CHANGED_EVENT = "ailechat:auth-session-changed";

export interface StoredSession extends AuthSession {
  /** localStorage 保存時の epoch ms (リフレッシュ判断用) */
  stored_at: number;
}

export function readStoredSession(): StoredSession | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

export function writeStoredSession(session: AuthSession | null): void {
  if (typeof localStorage === "undefined") return;
  if (!session) {
    clearAuthStorage();
  } else {
    if(!session.access_token||!session.refresh_token||!session.user?.id)throw new AuthApiError(400,'SESSION_INCOMPLETE','Complete authentication before storing a session');
    retireLegacyAuthStorage();
    // Factor enrollment responses include one-time recovery codes. Never persist extra fields.
    const stored: StoredSession = {access_token:session.access_token,refresh_token:session.refresh_token,token_type:session.token_type,expires_at:session.expires_at,refresh_expires_at:session.refresh_expires_at,user:session.user,stored_at:Date.now()};
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_CHANGED_EVENT, { detail: session }));
  }
}

/**
 * URL fragment (`#access_token=...&refresh_token=...&expires_at=...`) から OAuth callback
 * のセッション情報を取り出す。
 */
export function parseOAuthFragment(hash: string): AuthSession | null {
  if (!hash) return null;
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(stripped);
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  const expires_at = params.get("expires_at");
  if (!access_token || !refresh_token || !expires_at) return null;
  // user 情報は access_token から取り出す (decode のみ、verify は API 側に任せる)
  let user: AuthSession["user"];
  try {
    const payload = decodeJwtPayload(access_token);
    user = {
      id: String(payload.sub ?? ""),
      tenant_id: String(payload.tid ?? ""),
      email: String(payload.email ?? ""),
      display_name: String(payload.email ?? "").split("@")[0],
      role: String(payload.role ?? "member"),
      last_sign_in_at: null,
      sign_in_method: params.get("provider") ?? null,
    };
  } catch {
    return null;
  }
  return {
    access_token,
    refresh_token,
    token_type: "Bearer",
    expires_at: Number(expires_at),
    refresh_expires_at: 0,
    user,
  };
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const [, payloadB64] = token.split(".");
  if (!payloadB64) throw new Error("malformed jwt");
  const b64 = payloadB64.replace(/-/g, "+").replace(/_/g, "/").padEnd(
    payloadB64.length + ((4 - (payloadB64.length % 4)) % 4),
    "=",
  );
  const json = atob(b64);
  return JSON.parse(decodeURIComponent(escape(json))) as Record<string, unknown>;
}
